import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import Icon from "./UI/Icon";
import { SITE } from "../data/site";
import emailjs from "@emailjs/browser";

const columns = [
  {
    title: "What we deliver",
    items: [
      "First fix framing, joists and stud walls",
      "Second fix doors, skirting, architraves and stairs",
      "Roof structures, trusses, fascias and soffits",
      "Decking, balconies and external cladding",
      "Bespoke joinery installs and kitchen fitting",
    ],
  },
  {
    title: "Site-ready credentials",
    items: [
      "CSCS-qualified, fully insured",
      "Method statements and risk assessments supplied",
      "Flexible labour to meet programme demands",
      "Clear daily communication with site management",
      "Clean, safe and organised work areas",
    ],
  },
  {
    title: "Who we work with",
    items: [
      "Main contractors on new-build sites",
      "Developers needing phased carpentry packages",
      "Architects and site managers wanting a dependable crew",
      "Property managers needing fast-turnaround remedials",
    ],
  },
];

export default function SiteCarpentry() {
  const formRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (event) => {
    event.preventDefault();
    emailjs
      .sendForm("service_r5bviyi", "template_fbd5858", formRef.current, {
        publicKey: "wz4LipO_3T-LIVb8l",
      })
      .then(
        () => setSubmitted(true),
        (error) => console.error("Form submission failed", error)
      );
  };

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Site Carpentry" },
  ];

  const inputClass =
    "bg-paper w-full p-3 border border-hairline focus:border-oak text-ink placeholder:text-bark/50";

  return (
    <>
      <Seo
        title="Site Carpentry & Subcontracting in Cambridge | Cambridge Carpenter"
        description="Experienced site carpenter for new builds and large developments in Cambridge. First and second fix, roofing, decking and joinery packages. CSCS-qualified and insured."
        canonical="https://www.cambridgecarpenter.co.uk/site-carpentry/"
        schema={breadcrumbSchema(crumbs)}
      />

      <PageHero
        eyebrow="New builds & developments"
        title="Site carpentry & subcontracting"
        intro="Reliable subcontract carpentry that keeps programmes on schedule, from first fix through to final snagging."
        crumbs={<Breadcrumbs items={crumbs} />}
      />

      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="prose-links max-w-3xl">
          <SectionHeading
            eyebrow="For site managers & developers"
            title="Dependable carpentry crews for your site"
          />
          <div className="text-bark text-lg leading-8 mt-6 space-y-5">
            <p>
              From first fix through to final snagging, we keep programmes on schedule with tidy,
              coordinated carpentry. We’re used to working alongside multiple trades, keeping health
              and safety tight, and delivering joinery that hands over cleanly.
            </p>
            <p>
              Based in Cambridge, we provide site carpentry across Cambridgeshire and nearby counties.
              For bespoke joinery and fitted furniture, explore our{" "}
              <Link to="/carpentry-services">carpentry services</Link>, or{" "}
              <Link to="/contact">get in touch</Link> to schedule a site walkthrough.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-12">
          {columns.map((col) => (
            <div key={col.title} className="bg-linen border border-hairline p-7">
              <h3 className="font-display font-semibold text-xl text-ink mb-4">{col.title}</h3>
              <ul className="space-y-3">
                {col.items.map((item) => (
                  <li key={item} className="flex gap-3 text-bark leading-7">
                    <Icon name="quote" className="w-5 h-5 text-oak shrink-0 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-linen border-y border-hairline">
        <div className="max-w-site mx-auto px-5 sm:px-10 py-16 md:py-20 lg:grid lg:grid-cols-2 lg:gap-12 items-start">
          <div className="mb-10 lg:mb-0">
            <SectionHeading eyebrow="Enquiries" title="Discuss your site requirements" />
            <p className="text-bark text-lg leading-8 mt-6 max-w-xl">
              Tell us about your programme, drawings and package requirements and we’ll respond with
              availability, resource and pricing.
            </p>
            <div className="mt-8 space-y-4">
              <a href={SITE.phoneHref} className="flex items-center gap-3 text-ink font-semibold text-lg">
                <Icon name="phone" className="w-5 h-5 text-oak" /> {SITE.phone}
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-ink font-semibold text-lg break-all">
                <Icon name="mail" className="w-5 h-5 text-oak" /> {SITE.email}
              </a>
            </div>
            {submitted && (
              <p className="bg-oak/10 border border-oak/30 text-ink px-4 py-3 mt-6 font-semibold">
                Thanks for your enquiry. We’ll get back to you shortly.
              </p>
            )}
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="bg-paper border border-hairline p-6 sm:p-8">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="name" className="block mb-2 text-sm font-medium">Your name</label>
                <input type="text" id="name" name="name" required className={inputClass} placeholder="Site contact" />
              </div>
              <div>
                <label htmlFor="company" className="block mb-2 text-sm font-medium">Company</label>
                <input type="text" id="company" name="company" className={inputClass} placeholder="Main contractor / developer" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="telephone" className="block mb-2 text-sm font-medium">Phone</label>
                <input type="tel" id="telephone" name="telephone" required className={inputClass} placeholder="07798 525115" />
              </div>
              <div>
                <label htmlFor="email" className="block mb-2 text-sm font-medium">Email</label>
                <input type="email" id="email" name="email" required className={inputClass} placeholder="you@company.com" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="location" className="block mb-2 text-sm font-medium">Site location</label>
                <input type="text" id="location" name="location" className={inputClass} placeholder="Cambridge / CB postcode" />
              </div>
              <div>
                <label htmlFor="startDate" className="block mb-2 text-sm font-medium">Start date</label>
                <input type="text" id="startDate" name="startDate" className={inputClass} placeholder="e.g. Late October" />
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="scope" className="block mb-2 text-sm font-medium">Scope of works</label>
              <input type="text" id="scope" name="subject" className={inputClass} placeholder="First/second fix, roofing, decking, kitchens..." />
            </div>
            <div className="mb-6">
              <label htmlFor="message" className="block mb-2 text-sm font-medium">Project details</label>
              <textarea id="message" name="message" rows="5" className={inputClass} placeholder="Programme, plots, drawings and any access requirements."></textarea>
            </div>
            <button type="submit" className="btn-primary w-full md:w-auto">Send enquiry</button>
          </form>
        </div>
      </section>
    </>
  );
}
